import React, { useState, useEffect } from "react";
import { Eye, Search, X, Filter, Send, ChevronLeft, ChevronRight, Edit2 } from "lucide-react";
import { getAllInventory, updateInventory } from "../../../services/Inventory/Inventory.service";
import { getAllSellers } from "../../../services/Users/User.service";
import axios from "axios";
import "./InventoryList.css";
import { environment } from "../../../environments/environment";

// ✅ Helper function to safely get images as array
const getImageArray = (images: any): string[] => {
    if (!images) return [];
    if (Array.isArray(images)) return images;
    if (typeof images === 'string') return [images];
    return [];
};

// ✅ Helper function to get full image URL
const getFullImageUrl = (imagePath: string): string => {
    if (!imagePath) return "";
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
        return imagePath;
    }
    if (imagePath.startsWith('data:')) {
        return imagePath;
    }
    const cleanPath = imagePath.startsWith('/') ? imagePath.slice(1) : imagePath;
    return `${environment.fileUrl}/${cleanPath}`;
};

const InventoryList: React.FC = () => {
    const [inventory, setInventory] = useState<any[]>([]);
    const [filteredInventory, setFilteredInventory] = useState<any[]>([]);
    const [currentPage, setCurrentPage] = useState(1);

    const [showModal, setShowModal] = useState(false);
    const [selectedItem, setSelectedItem] = useState<any>(null);
    const [searchTerm, setSearchTerm] = useState("");

    const [sellers, setSellers] = useState<any[]>([]);
    const [selectedSeller, setSelectedSeller] = useState("");

    // Image Gallery Modal State
    const [galleryModalOpen, setGalleryModalOpen] = useState(false);
    const [galleryImages, setGalleryImages] = useState<string[]>([]);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);

    // Dropdown data states
    const [metalTypes, setMetalTypes] = useState<any[]>([]);
    const [categories, setCategories] = useState<any[]>([]);
    const [articles, setArticles] = useState<any[]>([]);
    const [filteredArticles, setFilteredArticles] = useState<any[]>([]);

    // Search Modal States
    const [showSearchModal, setShowSearchModal] = useState(false);
    const [isSearching, setIsSearching] = useState(false);
    const [searchResults, setSearchResults] = useState<any[]>([]);

    // UPDATE INVENTORY MODAL STATE
    const [showUpdateModal, setShowUpdateModal] = useState(false);
    const [updateItem, setUpdateItem] = useState<any>(null);
    const [updateForm, setUpdateForm] = useState({ quantity: "", weight: "" });
    const [isUpdating, setIsUpdating] = useState(false);
    const [updateError, setUpdateError] = useState("");
    const [updateSuccess, setUpdateSuccess] = useState("");

    // Search Form Fields
    const [searchFilters, setSearchFilters] = useState({
        articleId: "",
        categoryId: "",
        metalTypeId: "",
        sellerId: "",
        businessName: "",
        purity: "",
        quantity: "",
        weight: "",
        wastage: "",
        makingCharges: "",
    });

    // Dropdown open states
    const [openMetalDropdown, setOpenMetalDropdown] = useState(false);
    const [openCategoryDropdown, setOpenCategoryDropdown] = useState(false);
    const [openArticleDropdown, setOpenArticleDropdown] = useState(false);
    const [openSellerDropdown, setOpenSellerDropdown] = useState(false);

    // Search terms for dropdowns
    const [searchMetal, setSearchMetal] = useState("");
    const [searchCategory, setSearchCategory] = useState("");
    const [searchArticle, setSearchArticle] = useState("");
    const [searchSeller, setSearchSeller] = useState("");

    // Selected display values
    const [selectedMetalDisplay, setSelectedMetalDisplay] = useState("");
    const [selectedCategoryDisplay, setSelectedCategoryDisplay] = useState("");
    const [selectedArticleDisplay, setSelectedArticleDisplay] = useState("");
    const [selectedSellerDisplay, setSelectedSellerDisplay] = useState("");

    // Single Global Search Key
    const [globalSearchKey, setGlobalSearchKey] = useState("");

    const itemsPerPage = 5;
    const startIndex = (currentPage - 1) * itemsPerPage;

    // Fetch dropdown data
    const fetchDropdownData = async () => {
        try {
            const metals = await axios.get("https://api.sonachandiwale.com/api/v1/metal/list");
            const categoriesRes = await axios.get("https://api.sonachandiwale.com/api/v1/categories");
            const articlesRes = await axios.get("https://api.sonachandiwale.com/api/v1/admin/articles");

            setMetalTypes(metals.data.data || []);
            setCategories(categoriesRes.data.data || []);
            setArticles(articlesRes.data.data || []);
            setFilteredArticles(articlesRes.data.data || []);
        } catch (error) {
            console.error("Dropdown API error", error);
        }
    };

    // Helper function to get all searchable values from an item
    const getAllSearchableValues = (item: any): string => {
        const values = [
            item.item?.articleName,
            item.buyers_sellers_detail?.businessName,
            item.item?.MetalTypeName,
            item.item?.category_name,
            item.item?.purity,
            item.item?.wastage,
            item.item?.makingCharges,
            item.id,
            item.itemId,
            item.totalQuantity,
            item.previousQuantity,
            item.AddedQuantity,
            item.soldQuantity,
            item.availableQuantity,
            item.totalWeight,
            item.availableWeight,
            item.userId,
        ];

        return values.filter(v => v !== null && v !== undefined).join(" ").toLowerCase();
    };

    // Fetch inventory
    const fetchInventory = async () => {
        try {
            const res = await getAllInventory();
            const data = res.data.data || [];
            setInventory(data);
            setFilteredInventory(data);
        } catch (error) {
            console.error("Error fetching inventory:", error);
        }
    };

    const fetchSellers = async () => {
        try {
            const res = await getAllSellers();
            setSellers(res || []);
        } catch (error) {
            console.error("Error fetching sellers:", error);
        }
    };

    // Apply all filters
    const applyAllFilters = () => {
        let results = [...inventory];

        if (globalSearchKey.trim()) {
            const searchLower = globalSearchKey.toLowerCase();
            results = results.filter((item) => {
                const allValues = getAllSearchableValues(item);
                return allValues.includes(searchLower);
            });
        }

        if (selectedSeller) {
            results = results.filter((item) =>
                item.buyers_sellers_detail?.businessName === selectedSeller
            );
        }

        if (searchFilters.metalTypeId) {
            const metalType = metalTypes.find(m => m.id === parseInt(searchFilters.metalTypeId));
            if (metalType) {
                results = results.filter((item) =>
                    item.item?.MetalTypeName === metalType.name
                );
            }
        }

        if (searchFilters.categoryId) {
            const category = categories.find(c => c.id === parseInt(searchFilters.categoryId));
            if (category) {
                results = results.filter((item) =>
                    item.item?.category_name === category.name
                );
            }
        }

        if (searchFilters.articleId) {
            const article = articles.find(a => a.id === parseInt(searchFilters.articleId));
            if (article) {
                results = results.filter((item) =>
                    item.item?.articleName === article.articleName
                );
            }
        }

        if (searchFilters.sellerId) {
            const seller = sellers.find(s => s.id === parseInt(searchFilters.sellerId));
            if (seller) {
                results = results.filter((item) =>
                    item.buyers_sellers_detail?.businessName === seller.businessName
                );
            }
        }

        if (searchFilters.businessName && !searchFilters.sellerId) {
            const filterLower = searchFilters.businessName.toLowerCase();
            results = results.filter((item) =>
                item.buyers_sellers_detail?.businessName?.toLowerCase().includes(filterLower)
            );
        }

        if (searchFilters.purity) {
            const filterLower = searchFilters.purity.toLowerCase();
            results = results.filter((item) =>
                item.item?.purity?.toLowerCase().includes(filterLower)
            );
        }

        if (searchFilters.quantity) {
            const quantityNum = parseFloat(searchFilters.quantity);
            results = results.filter((item) =>
                item.availableQuantity === quantityNum
            );
        }

        if (searchFilters.weight) {
            const weightNum = parseFloat(searchFilters.weight);
            results = results.filter((item) =>
                item.availableWeight === weightNum
            );
        }

        if (searchFilters.wastage) {
            const wastageNum = parseFloat(searchFilters.wastage);
            results = results.filter((item) =>
                item.item?.wastage === wastageNum
            );
        }

        if (searchFilters.makingCharges) {
            const makingChargesNum = parseFloat(searchFilters.makingCharges);
            results = results.filter((item) =>
                item.item?.makingCharges === makingChargesNum
            );
        }

        setFilteredInventory(results);
        setCurrentPage(1);
    };

    useEffect(() => {
        applyAllFilters();
    }, [globalSearchKey, selectedSeller, searchFilters, inventory]);

    useEffect(() => {
        fetchInventory();
        fetchSellers();
        fetchDropdownData();
    }, []);

    // Filter articles based on metal and category
    useEffect(() => {
        if (searchFilters.metalTypeId && searchFilters.categoryId) {
            const filtered = articles.filter(
                (a: any) =>
                    a.metalTypeId === parseInt(searchFilters.metalTypeId) &&
                    a.categoryId === parseInt(searchFilters.categoryId)
            );
            setFilteredArticles(filtered);
        } else if (searchFilters.metalTypeId) {
            const filtered = articles.filter(
                (a: any) => a.metalTypeId === parseInt(searchFilters.metalTypeId)
            );
            setFilteredArticles(filtered);
        } else if (searchFilters.categoryId) {
            const filtered = articles.filter(
                (a: any) => a.categoryId === parseInt(searchFilters.categoryId)
            );
            setFilteredArticles(filtered);
        } else {
            setFilteredArticles(articles);
        }
    }, [searchFilters.metalTypeId, searchFilters.categoryId, articles]);

    // ✅ FIXED: Open Image Gallery with safe image array
    const openGallery = (images: any) => {
        const imageArray = getImageArray(images);
        if (!imageArray || imageArray.length === 0) {
            return;
        }
        setGalleryImages(imageArray);
        setCurrentImageIndex(0);
        setGalleryModalOpen(true);
    };

    // Navigate in Gallery
    const nextImage = () => {
        setCurrentImageIndex((prev) => (prev + 1) % galleryImages.length);
    };

    const prevImage = () => {
        setCurrentImageIndex((prev) => (prev - 1 + galleryImages.length) % galleryImages.length);
    };

    const openModal = (item: any) => {
        setSelectedItem(item);
        setShowModal(true);
    };

    const openSearchModal = () => {
        setShowSearchModal(true);
        setSearchResults([]);
    };

    const closeSearchModal = () => {
        setShowSearchModal(false);
        setSearchResults([]);
    };

    // OPEN UPDATE MODAL
    const openUpdateModal = (item: any) => {
        setUpdateItem(item);
        setUpdateForm({ quantity: "", weight: "" });
        setUpdateError("");
        setUpdateSuccess("");
        setShowUpdateModal(true);
    };

    const closeUpdateModal = () => {
        setShowUpdateModal(false);
        setUpdateItem(null);
        setUpdateForm({ quantity: "", weight: "" });
        setUpdateError("");
        setUpdateSuccess("");
    };

    // HANDLE UPDATE INVENTORY SUBMIT
    const handleUpdateInventory = async () => {
        if (!updateForm.quantity && !updateForm.weight) {
            setUpdateError("Please enter at least quantity or weight to update.");
            return;
        }

        const quantity = updateForm.quantity ? parseFloat(updateForm.quantity) : undefined;
        const weight = updateForm.weight ? parseFloat(updateForm.weight) : undefined;

        if (quantity !== undefined && (isNaN(quantity) || quantity <= 0)) {
            setUpdateError("Quantity must be a positive number.");
            return;
        }
        if (weight !== undefined && (isNaN(weight) || weight <= 0)) {
            setUpdateError("Weight must be a positive number.");
            return;
        }

        setIsUpdating(true);
        setUpdateError("");
        setUpdateSuccess("");

        try {
            const payload: any = {
                userId: updateItem.userId,
                itemId: updateItem.itemId,
            };
            if (quantity !== undefined) payload.quantity = quantity;
            if (weight !== undefined) payload.weight = weight;

            await updateInventory(payload);

            setUpdateSuccess("Inventory updated successfully!");
            await fetchInventory();

            setTimeout(() => {
                closeUpdateModal();
            }, 1500);
        } catch (error: any) {
            console.error("Update inventory error:", error);
            setUpdateError(
                error?.response?.data?.message || "Failed to update inventory. Please try again."
            );
        } finally {
            setIsUpdating(false);
        }
    };

    // Handle filter input change
    const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setSearchFilters(prev => ({ ...prev, [name]: value }));
        setSearchResults([]);
    };

    // Metal selection handler
    const handleMetalSelect = (metal: any) => {
        setSearchFilters(prev => ({ ...prev, metalTypeId: metal.id.toString() }));
        setSelectedMetalDisplay(metal.name);
        setOpenMetalDropdown(false);
        setSearchMetal("");
    };

    // Category selection handler
    const handleCategorySelect = (category: any) => {
        setSearchFilters(prev => ({ ...prev, categoryId: category.id.toString() }));
        setSelectedCategoryDisplay(category.name);
        setOpenCategoryDropdown(false);
        setSearchCategory("");
    };

    // Article selection handler
    const handleArticleSelect = (article: any) => {
        setSearchFilters(prev => ({ ...prev, articleId: article.id.toString() }));
        setSelectedArticleDisplay(article.articleName);
        setOpenArticleDropdown(false);
        setSearchArticle("");
    };

    // Seller selection handler
    const handleSellerSelect = (seller: any) => {
        setSearchFilters(prev => ({
            ...prev,
            sellerId: seller.id.toString(),
            businessName: seller.businessName
        }));
        setSelectedSellerDisplay(seller.businessName);
        setOpenSellerDropdown(false);
        setSearchSeller("");
    };

    // Reset all filters
    const resetFilters = () => {
        setSearchFilters({
            articleId: "",
            categoryId: "",
            metalTypeId: "",
            sellerId: "",
            businessName: "",
            purity: "",
            quantity: "",
            weight: "",
            wastage: "",
            makingCharges: "",
        });
        setSelectedMetalDisplay("");
        setSelectedCategoryDisplay("");
        setSelectedArticleDisplay("");
        setSelectedSellerDisplay("");
        setGlobalSearchKey("");
        setSelectedSeller("");
        setSearchTerm("");
        setSearchResults([]);
        closeSearchModal();
        setTimeout(() => {
            setFilteredInventory(inventory);
        }, 100);
    };

    // Perform advanced search
    const performAdvancedSearch = () => {
        setIsSearching(true);

        setTimeout(() => {
            let results = [...inventory];

            if (searchFilters.metalTypeId) {
                const metalType = metalTypes.find(m => m.id === parseInt(searchFilters.metalTypeId));
                if (metalType) {
                    results = results.filter((item) =>
                        item.item?.MetalTypeName === metalType.name
                    );
                }
            }

            if (searchFilters.categoryId) {
                const category = categories.find(c => c.id === parseInt(searchFilters.categoryId));
                if (category) {
                    results = results.filter((item) =>
                        item.item?.category_name === category.name
                    );
                }
            }

            if (searchFilters.articleId) {
                const article = articles.find(a => a.id === parseInt(searchFilters.articleId));
                if (article) {
                    results = results.filter((item) =>
                        item.item?.articleName === article.articleName
                    );
                }
            }

            if (searchFilters.sellerId) {
                const seller = sellers.find(s => s.id === parseInt(searchFilters.sellerId));
                if (seller) {
                    results = results.filter((item) =>
                        item.buyers_sellers_detail?.businessName === seller.businessName
                    );
                }
            }

            if (searchFilters.businessName && !searchFilters.sellerId) {
                const filterLower = searchFilters.businessName.toLowerCase();
                results = results.filter((item) =>
                    item.buyers_sellers_detail?.businessName?.toLowerCase().includes(filterLower)
                );
            }

            if (searchFilters.purity) {
                const filterLower = searchFilters.purity.toLowerCase();
                results = results.filter((item) =>
                    item.item?.purity?.toLowerCase().includes(filterLower)
                );
            }

            if (searchFilters.quantity) {
                const quantityNum = parseFloat(searchFilters.quantity);
                results = results.filter((item) =>
                    item.availableQuantity === quantityNum
                );
            }

            if (searchFilters.weight) {
                const weightNum = parseFloat(searchFilters.weight);
                results = results.filter((item) =>
                    item.availableWeight === weightNum
                );
            }

            if (searchFilters.wastage) {
                const wastageNum = parseFloat(searchFilters.wastage);
                results = results.filter((item) =>
                    item.item?.wastage === wastageNum
                );
            }

            if (searchFilters.makingCharges) {
                const makingChargesNum = parseFloat(searchFilters.makingCharges);
                results = results.filter((item) =>
                    item.item?.makingCharges === makingChargesNum
                );
            }

            setSearchResults(results);
            setIsSearching(false);
        }, 300);
    };

    // Navigate to item
    const navigateToItem = (item: any) => {
        let filtered = [...inventory];

        if (globalSearchKey) {
            const searchLower = globalSearchKey.toLowerCase();
            filtered = filtered.filter((invItem) => {
                const allValues = getAllSearchableValues(invItem);
                return allValues.includes(searchLower);
            });
        }

        if (selectedSeller) {
            filtered = filtered.filter((invItem) =>
                invItem.buyers_sellers_detail?.businessName === selectedSeller
            );
        }

        const index = filtered.findIndex(i => i.id === item.id);

        if (index !== -1) {
            const pageNumber = Math.floor(index / itemsPerPage) + 1;
            setCurrentPage(pageNumber);
            closeSearchModal();

            setTimeout(() => {
                const element = document.getElementById(`inventory-row-${item.id}`);
                if (element) {
                    element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    element.style.backgroundColor = '#facc1520';
                    setTimeout(() => {
                        element.style.backgroundColor = '';
                    }, 2000);
                }
            }, 200);
        }
    };

    const clearGlobalSearch = () => {
        setGlobalSearchKey("");
        setSearchTerm("");
    };

    // Handle Send to Deal Calculator
    const handleSendToDealCalculator = (item: any) => {
        const dealData = {
            sellerId: item.userId,
            sellerName: `${item.buyers_sellers_detail?.firstName || ''} ${item.buyers_sellers_detail?.lastName || ''}`.trim(),
            metalType: item.item?.MetalTypeName,
            category: item.item?.category_name,
            articleName: item.item?.articleName,
            purity: item.item?.purity,
            wastage: item.item?.wastage,
            makingCharges: item.item?.makingCharges,
            inventoryId: item.id,
            itemId: item.itemId,
            availableQuantity: item.availableQuantity,
            availableWeight: item.availableWeight,
        };

        const encodedData = encodeURIComponent(JSON.stringify(dealData));
        window.location.href = `/deal-calculator?data=${encodedData}`;
    };

    const totalPages = Math.ceil(filteredInventory.length / itemsPerPage);

    const paginatedData = filteredInventory
        .slice(startIndex, startIndex + itemsPerPage)
        .map((item, index) => ({
            ...item,
            serial: startIndex + index + 1,
        }));

    // Filtered lists for dropdowns
    const filteredMetalsList = metalTypes.filter((metal) =>
        metal.name.toLowerCase().includes(searchMetal.toLowerCase())
    );

    const filteredCategoriesList = categories.filter((cat) =>
        cat.name.toLowerCase().includes(searchCategory.toLowerCase())
    );

    const filteredArticlesList = filteredArticles.filter((article) =>
        article.articleName.toLowerCase().includes(searchArticle.toLowerCase())
    );

    const filteredSellersList = sellers.filter((seller) =>
        seller.businessName?.toLowerCase().includes(searchSeller.toLowerCase())
    );

    return (
        <div className="employee-container">
            <div className="header-bar">
                <div className="search-section">
                    <div className="global-search-container">
                        <Search size={18} className="global-search-icon" />
                        <input
                            type="text"
                            placeholder="🔍 Global Search: Search ANY value..."
                            value={globalSearchKey}
                            onChange={(e) => {
                                setGlobalSearchKey(e.target.value);
                                setCurrentPage(1);
                            }}
                            className="global-search-input"
                        />
                        {globalSearchKey && (
                            <button onClick={clearGlobalSearch} className="clear-search-btn">
                                <X size={16} />
                            </button>
                        )}
                    </div>

                    <div className="filter-row">
                        <select
                            value={selectedSeller}
                            onChange={(e) => {
                                setSelectedSeller(e.target.value);
                                setCurrentPage(1);
                            }}
                            className="seller-filter-select"
                        >
                            <option value="">All Sellers</option>
                            {sellers.map((seller) => (
                                <option key={seller.id} value={seller.businessName}>
                                    {seller.businessName}
                                </option>
                            ))}
                        </select>

                        <button onClick={openSearchModal} className="advanced-search-btn">
                            <Filter size={16} />
                            Advanced Filters
                        </button>
                    </div>
                </div>
            </div>

            <div className="results-count-bar">
                <span>📊 Total Results: {filteredInventory.length} items</span>
                {globalSearchKey && (
                    <span className="active-filter-badge">🔍 Searching: "{globalSearchKey}"</span>
                )}
                {selectedSeller && (
                    <span className="active-filter-badge">🏪 Seller: {selectedSeller}</span>
                )}
            </div>

            <div className="table-wrapper">
                <table className="employee-table">
                    <thead>
                        <tr>
                            <th>S.No</th>
                            <th>Image</th>
                            <th>Article Name</th>
                            <th>Seller Name</th>
                            <th>Mobile Number</th>
                            <th>Address</th>
                            <th>Business Name</th>
                            <th>Metal Type</th>
                            <th>Category</th>
                            <th>Purity</th>
                            <th>Wastage</th>
                            <th>Making Charges</th>
                            <th>Weight Per article</th>
                            <th>Added Qty</th>
                            <th>Sold Qty</th>
                            <th>Available Qty</th>
                            <th>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {paginatedData.length > 0 ? (
                            paginatedData.map((item) => {
                                // ✅ FIXED: Get image array safely
                                const images = getImageArray(item.item?.image);
                                const firstImage = images.length > 0 ? images[0] : null;

                                const fullAddress = item.buyers_sellers_detail?.address || "N/A";
                                const truncatedAddress = fullAddress.length > 20
                                    ? fullAddress.substring(0, 20) + "..."
                                    : fullAddress;

                                return (
                                    <tr key={item.id} id={`inventory-row-${item.id}`}>
                                        <td>{item.serial}</td>
                                        <td>
                                            {firstImage ? (
                                                <img
                                                    src={getFullImageUrl(firstImage)}
                                                    alt="product"
                                                    className="inventory-product-img"
                                                    onClick={() => openGallery(images)}
                                                    onError={(e) => {
                                                        const target = e.target as HTMLImageElement;
                                                        target.style.display = 'none';
                                                        const parent = target.parentElement;
                                                        if (parent) {
                                                            const fallback = document.createElement('span');
                                                            fallback.textContent = 'No Image';
                                                            fallback.style.color = '#9ca3af';
                                                            fallback.style.fontSize = '12px';
                                                            parent.appendChild(fallback);
                                                        }
                                                    }}
                                                />
                                            ) : (
                                                <div className="no-image-placeholder">No Image</div>
                                            )}
                                        </td>
                                        <td>{item.item?.article_master?.articleName || "N/A"}</td>
                                        <td>{item.buyers_sellers_detail?.firstName + " " + item.buyers_sellers_detail?.lastName || "N/A"}</td>
                                        <td>{item.buyers_sellers_detail?.mobileNumber || "N/A"}</td>
                                        <td className="address-cell" data-full-address={fullAddress} style={{ cursor: "pointer", position: "relative" }}>
                                            {truncatedAddress}
                                            <span className="address-tooltip">{fullAddress}</span>
                                        </td>
                                        <td>{item.buyers_sellers_detail?.businessName || "N/A"}</td>
                                        <td>{item.item?.MetalTypeName || "N/A"}</td>
                                        <td>{item.item?.category?.name || "N/A"}</td>
                                        <td>{item.item?.purity || "N/A"}</td>
                                        <td>{item.item?.wastage || "N/A"}%</td>
                                        <td>₹{item.item?.makingCharges || "N/A"}/g</td>
                                        <td>₹{item.item?.weight || "N/A"}{item.item?.uom?.name || ""}</td>
                                        <td>{item.AddedQuantity}{item.item?.uom?.name || ""}</td>
                                        <td>{item.soldQuantity}{item.item?.uom?.name || ""}</td>
                                        <td>{item.availableQuantity}{item.item?.uom?.name || ""}</td>
                                        <td>
                                            <div className="action-buttons">
                                                <button onClick={() => openModal(item)} className="action-btn view-btn" title="View Details">
                                                    <Eye size={18} />
                                                </button>
                                                <button
                                                    onClick={() => openUpdateModal(item)}
                                                    className="action-btn edit-btn"
                                                    title="Update Inventory"
                                                >
                                                    <Edit2 size={18} />
                                                </button>
                                                <button
                                                    onClick={() => handleSendToDealCalculator(item)}
                                                    className="action-btn send-btn"
                                                    title="Send to Deal Calculator"
                                                >
                                                    <Send size={18} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })
                        ) : (
                            <tr><td colSpan={17} className="no-results-cell">
                                <div className="no-results-message">
                                    <Search size={48} strokeWidth={1} />
                                    <p>No results found</p>
                                </div>
                            </td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            {totalPages > 1 && (
                <div className="pagination">
                    <button onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))} disabled={currentPage === 1}>Previous</button>
                    <span>Page {currentPage} of {totalPages || 1}</span>
                    <button onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))} disabled={currentPage === totalPages}>Next</button>
                </div>
            )}

            {/* DETAIL MODAL */}
            {showModal && selectedItem && (
                <div className="modal-overlay" onClick={() => setShowModal(false)}>
                    <div className="modal-box" style={{ width: "800px" }} onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>Inventory Details</h3>
                            <button onClick={() => setShowModal(false)} className="close-modal-btn"><X size={20} /></button>
                        </div>

                        {/* ✅ FIXED: Modal images with safe array */}
                        {(() => {
                            const modalImages = getImageArray(selectedItem.item?.image);
                            if (modalImages.length > 0) {
                                return (
                                    <div className="modal-images-section">
                                        <label>Product Images:</label>
                                        <div className="modal-images-grid">
                                            {modalImages.map((img: string, idx: number) => (
                                                <img
                                                    key={idx}
                                                    src={getFullImageUrl(img)}
                                                    alt={`Product ${idx + 1}`}
                                                    className="modal-thumbnail"
                                                    onClick={() => openGallery(modalImages)}
                                                    onError={(e) => {
                                                        const target = e.target as HTMLImageElement;
                                                        target.style.display = 'none';
                                                    }}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                );
                            }
                            return null;
                        })()}

                        <table className="details-table">
                            <tbody>
                                <tr><td><strong>Item ID</strong></td><td>{selectedItem.itemId}</td></tr>
                                <tr><td><strong>User ID</strong></td><td>{selectedItem.userId}</td></tr>
                                <tr><td><strong>Total Quantity</strong></td><td>{selectedItem.totalQuantity}</td></tr>
                                <tr><td><strong>Previous Quantity</strong></td><td>{selectedItem.previousQuantity}</td></tr>
                                <tr><td><strong>Added Quantity</strong></td><td>{selectedItem.AddedQuantity}</td></tr>
                                <tr><td><strong>Sold Quantity</strong></td><td>{selectedItem.soldQuantity}</td></tr>
                                <tr><td><strong>Available Quantity</strong></td><td>{selectedItem.availableQuantity}</td></tr>
                                <tr><td><strong>Article</strong></td><td>{selectedItem.item?.articleName || "-"}</td></tr>
                                <tr><td><strong>Purity</strong></td><td>{selectedItem.item?.purity}%</td></tr>
                                <tr><td><strong>Wastage</strong></td><td>{selectedItem.item?.wastage}%</td></tr>
                                <tr><td><strong>Making Charges</strong></td><td>₹{selectedItem.item?.makingCharges}/g</td></tr>
                                <tr><td><strong>Description</strong></td><td>{selectedItem.item?.description || "-"}</td></tr>
                            </tbody>
                        </table>
                        <div style={{ marginTop: "15px", textAlign: "right", display: "flex", gap: "10px", justifyContent: "flex-end" }}>
                            <button onClick={() => { setShowModal(false); openUpdateModal(selectedItem); }} className="edit-deal-btn">
                                <Edit2 size={16} /> Update Inventory
                            </button>
                            <button onClick={() => handleSendToDealCalculator(selectedItem)} className="send-deal-btn">
                                <Send size={16} /> Send to Deal Calculator
                            </button>
                            <button onClick={() => setShowModal(false)} className="close-modal-footer-btn">Close</button>
                        </div>
                    </div>
                </div>
            )}

            {/* UPDATE INVENTORY MODAL */}
            {showUpdateModal && updateItem && (
                <div className="modal-overlay" onClick={closeUpdateModal}>
                    <div className="modal-box" style={{ width: "480px" }} onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3><Edit2 size={18} style={{ marginRight: "8px" }} />Update Inventory</h3>
                            <button onClick={closeUpdateModal} className="close-modal-btn"><X size={20} /></button>
                        </div>

                        <div style={{ padding: "16px 0" }}>
                            {/* ✅ FIXED: Update modal images with safe array */}
                            <div className="update-info-row" style={{ flexDirection: "column", alignItems: "flex-start" }}>
                                <span className="update-info-label">Article Image:</span>
                                <div style={{ display: "flex", gap: "8px", marginTop: "6px", flexWrap: "wrap" }}>
                                    {(() => {
                                        const updateImages = getImageArray(updateItem.item?.image);
                                        if (updateImages.length > 0) {
                                            return updateImages.map((img: any, index: any) => (
                                                <img
                                                    key={index}
                                                    src={getFullImageUrl(img)}
                                                    alt="article"
                                                    style={{
                                                        width: "70px",
                                                        height: "70px",
                                                        objectFit: "cover",
                                                        borderRadius: "6px",
                                                        border: "1px solid #ddd",
                                                    }}
                                                    onError={(e) => {
                                                        const target = e.target as HTMLImageElement;
                                                        target.style.display = 'none';
                                                    }}
                                                />
                                            ));
                                        }
                                        return <span className="update-info-value">No Image</span>;
                                    })()}
                                </div>
                            </div>

                            <div className="update-modal-item-info">
                                <div className="update-info-row">
                                    <span className="update-info-label">Article:</span>
                                    <span className="update-info-value">{updateItem.item?.articleName || "N/A"}</span>
                                </div>
                                <div className="update-info-row">
                                    <span className="update-info-label">Seller Name:</span>
                                    <span className="update-info-value">
                                        {updateItem.buyers_sellers_detail
                                            ? `${updateItem.buyers_sellers_detail.firstName || ""} ${updateItem.buyers_sellers_detail.lastName || ""}`.trim()
                                            : "N/A"}
                                    </span>
                                </div>
                                <div className="update-info-row">
                                    <span className="update-info-label">Business Name:</span>
                                    <span className="update-info-value">{updateItem.buyers_sellers_detail?.businessName || "N/A"}</span>
                                </div>
                                <div className="update-info-row">
                                    <span className="update-info-label">Business Type:</span>
                                    <span className="update-info-value">{updateItem.buyers_sellers_detail?.user_type || "N/A"}</span>
                                </div>
                                <div className="update-info-row">
                                    <span className="update-info-label">Metal Type:</span>
                                    <span className="update-info-value">{updateItem.item?.MetalTypeName || "N/A"}</span>
                                </div>
                                <div className="update-info-row">
                                    <span className="update-info-label">Category:</span>
                                    <span className="update-info-value">{updateItem.item?.category_name || "N/A"}</span>
                                </div>
                                <div className="update-info-row">
                                    <span className="update-info-label">Purity:</span>
                                    <span className="update-info-value">{updateItem.item?.purity || "N/A"}</span>
                                </div>
                                <div className="update-info-row">
                                    <span className="update-info-label">Wastage:</span>
                                    <span className="update-info-value">{updateItem.item?.wastage || "N/A"}%</span>
                                </div>
                                <div className="update-info-row">
                                    <span className="update-info-label">Making Charges:</span>
                                    <span className="update-info-value">{updateItem.item?.makingCharges || "N/A"}/g</span>
                                </div>
                                <div className="update-info-row">
                                    <span className="update-info-label">Total Qty:</span>
                                    <span className="update-info-value">{updateItem.totalQuantity || "N/A"}</span>
                                </div>
                                <div className="update-info-row">
                                    <span className="update-info-label">Prev Qty:</span>
                                    <span className="update-info-value">{updateItem.previousQuantity || "N/A"}</span>
                                </div>
                                <div className="update-info-row">
                                    <span className="update-info-label">Added Qty:</span>
                                    <span className="update-info-value">{updateItem.AddedQuantity || "N/A"}</span>
                                </div>
                                <div className="update-info-row">
                                    <span className="update-info-label">Sold Qty:</span>
                                    <span className="update-info-value">{updateItem.soldQuantity || "0"}</span>
                                </div>
                                <div className="update-info-row">
                                    <span className="update-info-label">Total Weight:</span>
                                    <span className="update-info-value">{updateItem.totalWeight || "0"}</span>
                                </div>
                                <div className="update-info-row">
                                    <span className="update-info-label">Current Available Qty:</span>
                                    <span className="update-info-value update-info-highlight">{updateItem.availableQuantity}</span>
                                </div>
                                <div className="update-info-row">
                                    <span className="update-info-label">Current Available Weight:</span>
                                    <span className="update-info-value update-info-highlight">{updateItem.availableWeight}g</span>
                                </div>
                            </div>

                            {/* Update Fields */}
                            <div className="update-modal-fields">
                                <div className="form-group">
                                    <label>Edit Quantity <span style={{ color: "#888", fontSize: "12px" }}>(will be Edited to existing)</span></label>
                                    <input
                                        type="number"
                                        value={updateForm.quantity}
                                        onChange={(e) => {
                                            setUpdateForm(prev => ({ ...prev, quantity: e.target.value }));
                                            setUpdateError("");
                                        }}
                                        placeholder="Enter quantity to Edit..."
                                        min="1"
                                        step="1"
                                    />
                                </div>
                            </div>

                            {updateError && (
                                <div className="update-modal-error">
                                    ⚠️ {updateError}
                                </div>
                            )}
                            {updateSuccess && (
                                <div className="update-modal-success">
                                    ✅ {updateSuccess}
                                </div>
                            )}
                        </div>

                        <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end", marginTop: "8px" }}>
                            <button onClick={closeUpdateModal} className="close-modal-footer-btn" disabled={isUpdating}>
                                Cancel
                            </button>
                            <button
                                onClick={handleUpdateInventory}
                                className="update-confirm-btn"
                                disabled={isUpdating}
                            >
                                {isUpdating ? "Updating..." : "Update Inventory"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* IMAGE GALLERY MODAL */}
            {galleryModalOpen && (
                <div className="modal-overlay gallery-modal" onClick={() => setGalleryModalOpen(false)}>
                    <div className="gallery-content" onClick={(e) => e.stopPropagation()}>
                        <button className="gallery-close" onClick={() => setGalleryModalOpen(false)}>
                            <X size={24} />
                        </button>

                        <div className="gallery-container">
                            <button
                                className="gallery-nav prev"
                                onClick={prevImage}
                                disabled={galleryImages.length <= 1}
                            >
                                <ChevronLeft size={36} />
                            </button>

                            <div className="gallery-image-wrapper">
                                {galleryImages.length > 0 && (
                                    <img
                                        src={getFullImageUrl(galleryImages[currentImageIndex] || "")}
                                        alt={`Product image ${currentImageIndex + 1}`}
                                        className="gallery-image"
                                        onError={(e) => {
                                            const target = e.target as HTMLImageElement;
                                            target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200"%3E%3Crect width="200" height="200" fill="%23f3f4f6"/%3E%3Ctext x="50" y="100" font-family="Arial" font-size="14" fill="%239ca3af"%3ENo Image%3C/text%3E%3C/svg%3E';
                                        }}
                                    />
                                )}
                                <div className="image-counter">
                                    {currentImageIndex + 1} / {galleryImages.length}
                                </div>
                            </div>

                            <button
                                className="gallery-nav next"
                                onClick={nextImage}
                                disabled={galleryImages.length <= 1}
                            >
                                <ChevronRight size={36} />
                            </button>
                        </div>

                        {galleryImages.length > 1 && (
                            <div className="thumbnail-container">
                                {galleryImages.map((img, idx) => (
                                    <img
                                        key={idx}
                                        src={getFullImageUrl(img)}
                                        alt={`Thumbnail ${idx + 1}`}
                                        className={`thumbnail ${idx === currentImageIndex ? 'active' : ''}`}
                                        onClick={() => setCurrentImageIndex(idx)}
                                        onError={(e) => {
                                            const target = e.target as HTMLImageElement;
                                            target.style.display = 'none';
                                        }}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* ADVANCED SEARCH MODAL - Keep as is */}
            {/* ADVANCED SEARCH MODAL */}
            {showSearchModal && (
                <div className="modal-overlay" onClick={closeSearchModal}>
                    <div className="modal-box search-modal" style={{ width: "1000px", maxWidth: "95%" }} onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3><Filter size={20} style={{ marginRight: "8px" }} />Advanced Filters</h3>
                            <button onClick={closeSearchModal} className="close-modal-btn"><X size={20} /></button>
                        </div>

                        <div className="search-form">
                            <div className="search-form-grid">
                                {/* Metal Type Dropdown */}
                                <div className="custom-dropdown-container">
                                    <label>Metal Type</label>
                                    <div className="custom-dropdown">
                                        <div className="custom-dropdown-select" onClick={() => setOpenMetalDropdown(!openMetalDropdown)}>
                                            {selectedMetalDisplay || "Select Metal Type"}
                                            <span className="dropdown-arrow">▼</span>
                                        </div>
                                        {openMetalDropdown && (
                                            <div className="custom-dropdown-menu">
                                                <input
                                                    type="text"
                                                    className="custom-dropdown-search"
                                                    placeholder="Search metal..."
                                                    value={searchMetal}
                                                    onChange={(e) => setSearchMetal(e.target.value)}
                                                    autoFocus
                                                />
                                                <div className="custom-dropdown-options">
                                                    {filteredMetalsList.length > 0 ? (
                                                        filteredMetalsList.map((metal) => (
                                                            <div key={metal.id} className="custom-dropdown-option" onClick={() => handleMetalSelect(metal)}>
                                                                {metal.name}
                                                            </div>
                                                        ))
                                                    ) : (
                                                        <div className="custom-dropdown-option disabled">No metals found</div>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Category Dropdown */}
                                <div className="custom-dropdown-container">
                                    <label>Category</label>
                                    <div className="custom-dropdown">
                                        <div className="custom-dropdown-select" onClick={() => setOpenCategoryDropdown(!openCategoryDropdown)}>
                                            {selectedCategoryDisplay || "Select Category"}
                                            <span className="dropdown-arrow">▼</span>
                                        </div>
                                        {openCategoryDropdown && (
                                            <div className="custom-dropdown-menu">
                                                <input
                                                    type="text"
                                                    className="custom-dropdown-search"
                                                    placeholder="Search category..."
                                                    value={searchCategory}
                                                    onChange={(e) => setSearchCategory(e.target.value)}
                                                    autoFocus
                                                />
                                                <div className="custom-dropdown-options">
                                                    {filteredCategoriesList.length > 0 ? (
                                                        filteredCategoriesList.map((cat) => (
                                                            <div key={cat.id} className="custom-dropdown-option" onClick={() => handleCategorySelect(cat)}>
                                                                {cat.name}
                                                            </div>
                                                        ))
                                                    ) : (
                                                        <div className="custom-dropdown-option disabled">No categories found</div>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Article Dropdown */}
                                <div className="custom-dropdown-container">
                                    <label>Article Name</label>
                                    <div className="custom-dropdown">
                                        <div className="custom-dropdown-select" onClick={() => setOpenArticleDropdown(!openArticleDropdown)}>
                                            {selectedArticleDisplay || "Select Article"}
                                            <span className="dropdown-arrow">▼</span>
                                        </div>
                                        {openArticleDropdown && (
                                            <div className="custom-dropdown-menu">
                                                <input
                                                    type="text"
                                                    className="custom-dropdown-search"
                                                    placeholder="Search article..."
                                                    value={searchArticle}
                                                    onChange={(e) => setSearchArticle(e.target.value)}
                                                    autoFocus
                                                />
                                                <div className="custom-dropdown-options">
                                                    {filteredArticlesList.length > 0 ? (
                                                        filteredArticlesList.map((article) => (
                                                            <div key={article.id} className="custom-dropdown-option" onClick={() => handleArticleSelect(article)}>
                                                                {article.articleName}
                                                            </div>
                                                        ))
                                                    ) : (
                                                        <div className="custom-dropdown-option disabled">No articles found</div>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Seller/Business Name Dropdown */}
                                <div className="custom-dropdown-container">
                                    <label>Business Name (Seller)</label>
                                    <div className="custom-dropdown">
                                        <div className="custom-dropdown-select" onClick={() => setOpenSellerDropdown(!openSellerDropdown)}>
                                            {selectedSellerDisplay || "Select Seller"}
                                            <span className="dropdown-arrow">▼</span>
                                        </div>
                                        {openSellerDropdown && (
                                            <div className="custom-dropdown-menu">
                                                <input
                                                    type="text"
                                                    className="custom-dropdown-search"
                                                    placeholder="Search seller..."
                                                    value={searchSeller}
                                                    onChange={(e) => setSearchSeller(e.target.value)}
                                                    autoFocus
                                                />
                                                <div className="custom-dropdown-options">
                                                    {filteredSellersList.length > 0 ? (
                                                        filteredSellersList.map((seller) => (
                                                            <div key={seller.id} className="custom-dropdown-option" onClick={() => handleSellerSelect(seller)}>
                                                                {seller.businessName} {seller.firstName && `(${seller.firstName} ${seller.lastName || ''})`}
                                                            </div>
                                                        ))
                                                    ) : (
                                                        <div className="custom-dropdown-option disabled">No sellers found</div>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Purity */}
                                <div className="form-group">
                                    <label>Purity</label>
                                    <input
                                        type="text"
                                        name="purity"
                                        value={searchFilters.purity}
                                        onChange={handleFilterChange}
                                        placeholder="22K, 24K, 92.5%..."
                                    />
                                </div>

                                {/* Quantity */}
                                <div className="form-group">
                                    <label>Available Quantity (Exact)</label>
                                    <input
                                        type="number"
                                        name="quantity"
                                        value={searchFilters.quantity}
                                        onChange={handleFilterChange}
                                        placeholder="Enter exact quantity..."
                                        step="1"
                                    />
                                </div>

                                {/* Weight */}
                                <div className="form-group">
                                    <label>Available Weight (Exact) (g)</label>
                                    <input
                                        type="number"
                                        name="weight"
                                        value={searchFilters.weight}
                                        onChange={handleFilterChange}
                                        placeholder="Enter exact weight..."
                                        step="0.01"
                                    />
                                </div>

                                {/* Wastage */}
                                <div className="form-group">
                                    <label>Wastage (%)</label>
                                    <input
                                        type="number"
                                        name="wastage"
                                        value={searchFilters.wastage}
                                        onChange={handleFilterChange}
                                        placeholder="Enter wastage..."
                                        step="0.01"
                                    />
                                </div>

                                {/* Making Charges */}
                                <div className="form-group">
                                    <label>Making Charges (₹/g)</label>
                                    <input
                                        type="number"
                                        name="makingCharges"
                                        value={searchFilters.makingCharges}
                                        onChange={handleFilterChange}
                                        placeholder="Enter making charges..."
                                        step="0.01"
                                    />
                                </div>
                            </div>

                            <div className="search-form-actions">
                                <button type="button" onClick={resetFilters} className="reset-btn">
                                    <X size={16} /> Reset All Filters
                                </button>
                                <button type="button" onClick={performAdvancedSearch} className="search-btn" disabled={isSearching}>
                                    {isSearching ? "Searching..." : "🔍 Search"}
                                </button>
                            </div>
                        </div>

                        {/* SEARCH RESULTS */}
                        {(searchResults.length > 0 || isSearching) && (
                            <div className="search-results-section">
                                <div className="search-results-header">
                                    <h4>📋 Search Results ({searchResults.length} found)</h4>
                                    {searchResults.length > 0 && (
                                        <button onClick={() => setSearchResults([])} className="clear-results-btn">
                                            <X size={16} /> Clear
                                        </button>
                                    )}
                                </div>
                                <div className="search-results-list">
                                    {isSearching ? (
                                        <div className="search-loading">
                                            <div className="loading-spinner"></div>
                                            Searching...
                                        </div>
                                    ) : searchResults.length > 0 ? (
                                        searchResults.map((result) => (
                                            <div key={result.id} className="search-result-item" onClick={() => navigateToItem(result)}>
                                                <div className="result-info">
                                                    <div className="result-title">
                                                        <strong>{result.item?.article_master?.articleName || result.item?.articleName || "N/A"}</strong>
                                                        <span className="result-business">{result.buyers_sellers_detail?.businessName || "N/A"}</span>
                                                        <span className="result-quantity">📦 {result.availableQuantity} available</span>
                                                    </div>
                                                    <div className="result-details-grid">
                                                        <span>⚖️ Weight: {result.availableWeight}g</span>
                                                        <span>🔩 Metal: {result.item?.MetalTypeName || "N/A"}</span>
                                                        <span>📁 Category: {result.item?.category?.name || result.item?.category_name || "N/A"}</span>
                                                        <span>💎 Purity: {result.item?.purity || "N/A"}</span>
                                                        <span>⚙️ Wastage: {result.item?.wastage || "N/A"}%</span>
                                                        <span>💰 Making: ₹{result.item?.makingCharges || "N/A"}/g</span>
                                                    </div>
                                                    {result.buyers_sellers_detail?.address && (
                                                        <div className="result-address">
                                                            📍 {result.buyers_sellers_detail.address}
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="result-action">
                                                    <button className="view-item-btn">Click to View →</button>
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <div className="no-search-results">
                                            <Search size={32} />
                                            <p>No items match your filters</p>
                                            <span className="hint">Try adjusting your search criteria</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default InventoryList;